import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ChartDefs,
  chartPalette,
  getChartIds,
  getTotalsBarColor,
  getTotalsBarHoverColor,
} from "@/components/common/chartStyles";
import { Pencil, Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { visitProject } from "@/features/onboarding/utils";
import { useNavigate } from "react-router-dom";

// Recharts
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
  ReferenceDot,
  CartesianGrid,
  Line,
  ComposedChart,
  LabelList,
  Cell,
} from "recharts";
import {
  calcNxplanProgress,
  normalizeNxplanDataForProgress,
  calculateTargetReduction,
  formatCompactNumber,
} from "@/features/onboarding/utils";
import { config } from "@/lib/constants";

const sumEmission = (arr) =>
  Array.isArray(arr)
    ? arr.reduce((s, it) => s + (Number(it?.emission) || 0), 0)
    : 0;

const panelClassName = "rounded-xl border p-3 dark:border-white/20";
const rowClassName = "flex items-center justify-between py-1";
const tileClassName = "rounded-lg bg-slate-50 p-3 dark:bg-white/5";
const textSmClassName = "text-sm";

const chartIds = getChartIds("nxplan");

const InfoRow = ({
  label,
  value,
  labelClassName = "",
  valueClassName = "",
}) => {
  const labelClasses = labelClassName
    ? `${textSmClassName} ${labelClassName}`
    : textSmClassName;
  const valueClasses = valueClassName
    ? `${textSmClassName} ${valueClassName}`
    : textSmClassName;

  return (
    <div className={rowClassName}>
      {label ? (
        <p variant="label" className={labelClasses}>
          {label}
        </p>
      ) : null}
      {value != null ? (
        <p variant="value" className={valueClasses}>
          {value}
        </p>
      ) : null}
    </div>
  );
};

const KpiTile = ({ label, value }) => (
  <div className={tileClassName}>
    <p variant="label" className="text-xs">
      {label}
    </p>
    <p variant="value" className="block">
      {value}
    </p>
  </div>
);

const NXPlanCard = ({ data = {}, onOpen, baseYear, organizationBoundary }) => {
  const { t } = useTranslation();
  const notSetLabel = t("Not set");
  const navigate = useNavigate();

  // Friendly labels for years (avoid empty strings)
  const baseYearLabel = baseYear ?? data.baseYear ?? notSetLabel;
  const reportingYearLabel = data.reportingYear ?? notSetLabel;
  const targetYearLabel = data.targetYear ?? notSetLabel;

  // -----------------------
  // Progress calculation (shared with form)
  // -----------------------
  const progress = useMemo(() => {
    const normalized = normalizeNxplanDataForProgress(
      data,
      baseYear,
      organizationBoundary,
    );
    return calcNxplanProgress(normalized);
  }, [data, baseYear, organizationBoundary]);

  // -----------------------
  // Numbers for chart & KPIs
  // -----------------------
  const {
    baselineValue = { scope1: 0, scope2: 0, scope3: 0 },
    scope1Emission,
    scope2Emission,
    scope3Emission,
  } = data || {};

  const baseline = {
    s1: Number(baselineValue.scope1) || 0,
    s2: Number(baselineValue.scope2) || 0,
    s3: Number(baselineValue.scope3) || 0,
  };
  const baselineTotal = baseline.s1 + baseline.s2 + baseline.s3;

  const reporting = {
    s1: sumEmission(scope1Emission?.data),
    s2: sumEmission(scope2Emission?.data),
    s3: sumEmission(scope3Emission?.data),
  };
  const reportingTotal = reporting.s1 + reporting.s2 + reporting.s3;

  // Determine target reduction percent
  const targetPercent = useMemo(() => {
    if (data.isSBTi === "yes") {
      const totalTarget = calculateTargetReduction(
        data.isSBTi,
        data.targetYear,
        baseYear,
        data.sbtiTemperature,
        data.regressionModel,
        data.targetReduction,
      );
      return parseFloat(totalTarget) || 0;
    } else {
      return parseFloat(data.targetReduction) || 0;
    }
  }, [data, baseYear, data.isSBTi]);

  const progressFooter = (
    <div className="flex items-center gap-3">
      <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-gradient-to-r from-emerald-400/20 to-blue-500/20">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );

  // Target absolute amount & target totals
  const targetAbsoluteAmount = baselineTotal * (targetPercent / 100) || 0;
  const targetTotal = Math.max(baselineTotal - targetAbsoluteAmount, 0);
  const targetPercentLabel = isNaN(targetPercent)
    ? notSetLabel
    : `${targetPercent.toFixed(2)} %`;
  const numberFormatter = formatCompactNumber;
  const [totalsHoverType, setTotalsHoverType] = useState(null);

  const baseKpiRows = [
    { label: t("Target Year"), value: data.targetYear ?? notSetLabel },
    { label: t("Reporting Year"), value: data.reportingYear ?? notSetLabel },
    {
      label: t("Reporting Emissions (tCO₂e)"),
      value: numberFormatter(reportingTotal),
      labelClassName: "text-blue-500 dark:text-blue-500",
    },
    { label: t("Scope 1"), value: reporting.s1 || notSetLabel },
    { label: t("Scope 2"), value: reporting.s2 || notSetLabel },
    { label: t("Scope 3"), value: reporting.s3 || notSetLabel },
  ];

  const reductionRows = [
    { label: t("Target Reduction"), value: targetPercentLabel },
    {
      label: t("Target Reduction Amount (tCO₂e)"),
      value: targetAbsoluteAmount.toFixed(3),
    },
  ];

  const kpiTiles = [
    {
      label: t("Baseline Total"),
      value: `${baselineTotal.toLocaleString()} ${t("tCO₂e")}`,
    },
    {
      label: t("Reporting Total"),
      value: `${reportingTotal.toLocaleString()} ${t("tCO₂e")}`,
    },
    {
      label: t("Target %"),
      value: isNaN(targetPercent) ? "-" : `${targetPercent.toFixed(2)} %`,
    },
    {
      label: t("Target Total"),
      value: `${targetTotal.toLocaleString()} ${t("tCO₂e")}`,
    },
  ];

  // -----------------------
  // Totals-only bars dataset
  // -----------------------
  const totalsBarData = [
    {
      name: `${t("Baseline")} (${baseYearLabel})`,
      value: baselineTotal,
      type: "Baseline",
    },
    {
      name: `${t("Reporting")} (${reportingYearLabel})`,
      value: reportingTotal,
      type: "Reporting",
    },
    {
      name: `${t("Target")} (${targetYearLabel})`,
      value: targetTotal,
      type: "Target",
    },
  ];

  // -----------------------
  // Reduction series (bars + line), with YoY delta & % from reporting
  // -----------------------
  const reductionSeries = useMemo(() => {
    const startYear = Number(data?.reportingYear);
    const endYear = Number(data?.targetYear);

    const reportingStr = String(data?.reportingYear ?? reportingYearLabel);
    const targetStr = String(data?.targetYear ?? targetYearLabel);

    if (
      !Number.isFinite(startYear) ||
      !Number.isFinite(endYear) ||
      endYear <= startYear
    ) {
      // Fallback to two points if years invalid or reversed
      return [
        {
          year: reportingStr,
          tCO2e: reportingTotal,
          deltaYoY: 0,
          pctFromReporting: 0,
        },
        {
          year: targetStr,
          tCO2e: targetTotal,
          deltaYoY: targetTotal - reportingTotal,
          pctFromReporting:
            reportingTotal === 0
              ? 0
              : ((targetTotal - reportingTotal) / reportingTotal) * 100,
        },
      ];
    }

    const years = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);

    const steps = years.length - 1 || 1;
    const series = years.map((y, idx) => {
      const fraction = idx / steps; // 0 at reporting, 1 at target
      const value = reportingTotal + (targetTotal - reportingTotal) * fraction;
      return { year: String(y), tCO2e: Math.max(value, 0) };
    });

    // add deltas & pct
    return series.map((pt, i) => {
      const prev = series[i - 1]?.tCO2e ?? pt.tCO2e;
      const deltaYoY = pt.tCO2e - prev;
      const pctFromReporting =
        reportingTotal === 0
          ? 0
          : ((pt.tCO2e - reportingTotal) / reportingTotal) * 100;
      return { ...pt, deltaYoY, pctFromReporting };
    });
  }, [
    data?.reportingYear,
    data?.targetYear,
    reportingTotal,
    targetTotal,
    reportingYearLabel,
    targetYearLabel,
  ]);

  // -----------------------
  // Helpers
  // -----------------------
  const TotalsTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const value = Number(payload[0]?.value || 0);
    const name = payload[0]?.payload?.name;

    return (
      <div className="rounded-md border bg-white/95 p-2 text-xs shadow-sm dark:border-white/10 dark:bg-slate-900/95">
        {name ? <div className="font-medium">{name}</div> : null}
        <div>
          {t("Total")}: {value.toLocaleString()} {t("tCO₂e")}
        </div>
      </div>
    );
  };

  const ReductionTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const v = Number(payload[0]?.value || 0);
    const p = payload[0]?.payload || {};
    const delta = Number(p.deltaYoY || 0);
    const pct = Number(p.pctFromReporting || 0);

    return (
      <div className="rounded-md border bg-white/95 p-2 text-xs shadow-sm dark:border-white/10 dark:bg-slate-900/95">
        <div className="font-medium">
          {t("Year")}: {label}
        </div>
        <div>
          {t("Total")}: {v.toLocaleString()} {t("tCO₂e")}
        </div>
        <div className={delta <= 0 ? "text-emerald-600" : "text-rose-600"}>
          {t("Δ vs prev")}: {delta >= 0 ? "+" : ""}
          {delta.toLocaleString()} {t("tCO₂e")}
        </div>
        <div className={pct <= 0 ? "text-emerald-600" : "text-rose-600"}>
          {t("% from reporting")}: {pct.toFixed(1)}%
        </div>
      </div>
    );
  };

  return (
    <>
      <Card className="rounded-radius pt-0">
        <CardHeader className="rounded-t-lg grid gap-2 py-3 bg-gradient-to-br from-slate-100 to-white dark:from-white/10 dark:to-white/5">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex justify-between items-center gap-2 text-base text-primary">
              <Target size={20} />
              {t("NXPlan - Carbon Neutral Planning")}
            </CardTitle>

            <div className="text-base text-primary flex items-center gap-3">
              <span className="text-sm font-semibold text-foreground">
                {progress}%
              </span>
              <Button
                size="sm"
                variant="secondary"
                className="bg-primary text-white hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/90"
                // className="transition-transform duration-200 hover:-translate-y-0.5"
                onClick={() => onOpen("nxplan", data)}
              >
                <Pencil className="!h-3 !w-3" />
                <span className="hidden sm:inline">{t("Edit Info")}</span>
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="bg-nxstart-green text-white hover:bg-nxstart-green/90 dark:bg-nxstart-green dark:hover:bg-nxstart-green/90"
                // onClick={() => visitProject("nxplan")}
                onClick={() =>
                  window.open(
                    config.nxplanBaseUr,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                {t("Visit NXPlan")}
              </Button>
            </div>
          </div>
          {progressFooter}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className={panelClassName}>
              {baseKpiRows.map((row) => (
                <InfoRow key={row.label} {...row} />
              ))}
            </div>

            <div className={panelClassName}>
              {reductionRows.map((row) => (
                <InfoRow key={row.label} {...row} />
              ))}
            </div>
          </div>
          <div className={panelClassName}>
            <div className="mb-2 flex items-center justify-between">
              <p
                variant="section"
                className="mb-0 text-sm text-blue-500 dark:text-blue-500"
              >
                {t("Emissions vs Target (Totals)")}
              </p>
              <p variant="caption" className="text-sm">
                {t("All values in tCO₂e")}
              </p>
            </div>

            <p variant="caption" className="mb-6 block text-sm">
              <span className="block sm:inline">
                {t("Base Year")}: {baseYearLabel}
              </span>
              <span className="hidden sm:inline"> • </span>
              <span className="block sm:inline">
                {t("Reporting Year")}: {reportingYearLabel}
              </span>
              <span className="hidden sm:inline"> • </span>
              <span className="block sm:inline">
                {t("Target Year")}: {targetYearLabel}
              </span>
            </p>

            <div className={`mb-4 h-44 w-full sm:h-56 ${textSmClassName}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={totalsBarData}
                  barGap={8}
                  margin={{ top: 8, right: 16, bottom: 0, left: 16 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartPalette.grid}
                  />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={numberFormatter} />
                  <Tooltip
                    content={<TotalsTooltip />}
                    cursor={{ fill: getTotalsBarHoverColor(totalsHoverType) }}
                  />
                  <Legend />

                  <Bar dataKey="value" name={t("Total")}>
                    {totalsBarData.map((entry, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={getTotalsBarColor(entry.type)}
                        onMouseEnter={() => setTotalsHoverType(entry.type)}
                        onMouseLeave={() => setTotalsHoverType(null)}
                      />
                    ))}
                  </Bar>

                  <ReferenceLine
                    ifOverflow="extendDomain"
                    y={targetTotal}
                    stroke={chartPalette.targetLine}
                    strokeDasharray="5 5"
                    label={{
                      value: `${t("Target Total")} (${targetYearLabel})`,
                      position: "insideBottomRight",
                      fill: chartPalette.targetLine,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Reduction Path: bars + line, target band, goal marker */}
            <div
              className={`mt-6 h-60 w-full sm:mt-6 sm:h-72 ${textSmClassName}`}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={reductionSeries}
                  margin={{ top: 8, right: 16, bottom: 0, left: 16 }}
                >
                  {/* Gradients & patterns */}
                  <ChartDefs
                    lineId={chartIds.lineFillId}
                    barPatternId={chartIds.barPatternId}
                    lineColor={chartPalette.line}
                    barColor={chartPalette.reporting}
                  />

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartPalette.grid}
                  />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={numberFormatter} />
                  <Tooltip content={<ReductionTooltip />} />
                  <Legend />

                  {/* Target band (±2% of target) */}
                  <ReferenceArea
                    y1={targetTotal * 0.98}
                    y2={targetTotal * 1.02}
                    fill={chartPalette.targetLine}
                    fillOpacity={0.07}
                    strokeOpacity={0}
                    ifOverflow="extendDomain"
                  />

                  {/* Target reference line */}
                  <ReferenceLine
                    y={targetTotal}
                    stroke={chartPalette.targetLine}
                    strokeDasharray="4 4"
                    label={{
                      value: `${t("Target")} (${targetYearLabel})`,
                      position: "insideBottomRight",
                      fill: chartPalette.targetLine,
                    }}
                    ifOverflow="extendDomain"
                  />

                  {/* Bars per year (rounded) */}
                  <Bar
                    dataKey="tCO2e"
                    name={t("Total Emissions")}
                    fill={`url(#${chartIds.barPatternId})`}
                    stroke={chartPalette.reporting}
                    strokeOpacity={0.6}
                    barSize={26}
                    radius={[6, 6, 0, 0]}
                  />

                  {/* Reduction line with area fill */}
                  <Line
                    type="monotoneX"
                    dataKey="tCO2e"
                    name={`${t("Reduction Path")} (${reportingYearLabel} → ${targetYearLabel})`}
                    stroke={chartPalette.line}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    fill={`url(#${chartIds.lineFillId})`}
                  >
                    <LabelList
                      position="top"
                      formatter={(v, _name, props) => {
                        const lastIdx = (reductionSeries?.length ?? 1) - 1;
                        const isEdge =
                          props?.index === 0 || props?.index === lastIdx;
                        if (!isEdge) return "";
                        return `${numberFormatter(v)} ${t("tCO₂e")}`;
                      }}
                    />
                  </Line>

                  {/* Highlight the goal point */}
                  <ReferenceDot
                    x={String(data?.targetYear ?? targetYearLabel)}
                    y={targetTotal}
                    r={4}
                    fill={chartPalette.targetLine}
                    stroke={chartPalette.targetLine}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* KPI tiles */}
            <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
              {kpiTiles.map((tile) => (
                <KpiTile key={tile.label} {...tile} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default NXPlanCard;
